import React, { useState, useEffect } from 'react';
import supabase from '../supabaseClientFixed';
import { 
  Users, TrendingUp, Calendar, School, Globe, Clock, 
  AlertCircle, CheckCircle, XCircle, Download, RefreshCw,
  ChevronDown, ChevronUp, Search, Filter, BarChart2,
  Mail, UserCheck, UserX, Award
} from 'lucide-react';

const AdminDashboard = ({ isOpen, onClose, user }) => {
  // Estados para datos
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    todayRegistrations: 0,
    weekRegistrations: 0,
    monthRegistrations: 0,
    activeTrials: 0,
    expiredTrials: 0,
    proUsers: 0
  });
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [countries, setCountries] = useState([]);
  
  // Estados para filtros y búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUniversity, setFilterUniversity] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterUserType, setFilterUserType] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  
  // Estados para UI
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedUser, setExpandedUser] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [trialDays, setTrialDays] = useState(7);
  const [restoringTrial, setRestoringTrial] = useState(false);

  // Verificar si es admin
  const isAdmin = user?.email === 'alexisgc90@gmail.com';

  useEffect(() => {
    if (isOpen && isAdmin) {
      loadDashboardData();
    }
  }, [isOpen, isAdmin]);

  useEffect(() => {
    applyFilters();
  }, [users, searchTerm, filterUniversity, filterStatus, filterUserType, sortBy]);

  const testSupabaseConnection = async () => {
    console.log('🧪 Iniciando test de conexión con Supabase...');
    
    try {
      // 1. Test: Verificar usuario autenticado
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      console.log('👤 Usuario actual:', currentUser?.email);
      
      // 2. Test: Contar registros en user_profiles
      const { count, error: countError } = await supabase
        .from('user_profiles')
        .select('*', { count: 'exact', head: true });
      
      if (countError) {
        console.error('❌ Error contando user_profiles:', countError);
      } else {
        console.log('📊 Total de registros en user_profiles:', count);
      }
      
      // 3. Test: Obtener un registro de muestra
      const { data: sample, error: sampleError } = await supabase
        .from('user_profiles')
        .select('*')
        .limit(1);
      
      if (sampleError) {
        console.error('❌ Error obteniendo muestra:', sampleError);
      } else {
        console.log('📋 Registro de muestra:', sample?.[0]);
        console.log('🔑 Campos disponibles:', sample?.[0] ? Object.keys(sample[0]) : 'No hay datos');
      }
      
      // 4. Test: Verificar si existe la función RPC
      const { data: rpcTest, error: rpcError } = await supabase
        .rpc('get_admin_stats');
      
      if (rpcError) {
        console.log('⚠️ Función get_admin_stats no disponible:', rpcError.message);
      } else {
        console.log('✅ Función get_admin_stats disponible:', rpcTest);
      }
      
    } catch (error) {
      console.error('❌ Error en test de conexión:', error);
    }
  };

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Primero hacer test de conexión
      await testSupabaseConnection();
      
      // Cargar estadísticas generales
      await loadStats();
      // Cargar lista de usuarios
      await loadUsers();
      // Cargar estadísticas por universidad
      await loadUniversityStats();
      // Cargar estadísticas por país
      await loadCountryStats();
    } catch (error) {
      console.error('Error cargando dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      // Usar la nueva función SQL get_admin_stats
      const { data, error } = await supabase
        .rpc('get_admin_stats');

      if (error) {
        console.error('Error al cargar estadísticas:', error);
        // Fallback a la versión anterior si la función no existe
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

        // Total de usuarios
        const { count: totalUsers } = await supabase
          .from('user_profiles')
          .select('*', { count: 'exact', head: true });

        // Registros de hoy
        const { count: todayRegistrations } = await supabase
          .from('user_profiles')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', today.toISOString());

        // Registros de la semana
        const { count: weekRegistrations } = await supabase
          .from('user_profiles')
          .select('*', { count: 'exact', head: true })
          .gte('created_at', weekAgo.toISOString());

        // Registros del mes
      const { count: monthRegistrations } = await supabase
        .from('user_profiles')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', monthAgo.toISOString());

      // Trials activos
      const { count: activeTrials } = await supabase
        .from('user_profiles')
        .select('*', { count: 'exact', head: true })
        .gt('trial_end_date', now.toISOString())
        .eq('subscription_status', 'trial');

      // Trials expirados
      const { count: expiredTrials } = await supabase
        .from('user_profiles')
        .select('*', { count: 'exact', head: true })
        .lt('trial_end_date', now.toISOString())
        .eq('subscription_status', 'trial');

      // Usuarios PRO
      const { count: proUsers } = await supabase
        .from('user_profiles')
        .select('*', { count: 'exact', head: true })
        .eq('subscription_status', 'pro');

        setStats({
          totalUsers: totalUsers || 0,
          todayRegistrations: todayRegistrations || 0,
          weekRegistrations: weekRegistrations || 0,
          monthRegistrations: monthRegistrations || 0,
          activeTrials: activeTrials || 0,
          expiredTrials: expiredTrials || 0,
          proUsers: proUsers || 0
        });
      } else if (data && data.length > 0) {
        // Usar datos de la función SQL
        const statsData = data[0];
        setStats({
          totalUsers: parseInt(statsData.total_users) || 0,
          todayRegistrations: parseInt(statsData.today_registrations) || 0,
          weekRegistrations: parseInt(statsData.week_registrations) || 0,
          monthRegistrations: parseInt(statsData.month_registrations) || 0,
          activeTrials: parseInt(statsData.active_trials) || 0,
          expiredTrials: parseInt(statsData.expired_trials) || 0,
          proUsers: parseInt(statsData.pro_users) || 0
        });
      }
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    }
  };

  const loadUsers = async () => {
    try {
      console.log('🔍 Iniciando carga de usuarios con emails...');
      
      // NUEVO: Intentar primero con la función simple que definitivamente trae emails
      let { data, error } = await supabase
        .rpc('get_all_users_with_emails');
      
      // Si falla, intentar con la vista admin_users_view
      if (error) {
        console.log('❌ get_all_users_with_emails falló:', error.message);
        console.log('Intentando con vista admin_users_view...');
        const result = await supabase
          .from('admin_users_view')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (!result.error && result.data) {
          // Mapear para usar auth_email si email está vacío
          data = result.data.map(user => ({
            ...user,
            email: user.email || user.auth_email
          }));
          error = null;
        } else {
          data = result.data;
          error = result.error;
        }
      }
      
      // Si falla, intentar con get_admin_users
      if (error) {
        console.log('❌ get_admin_users_v2 falló:', error.message);
        console.log('Intentando con get_admin_users...');
        const result = await supabase
          .rpc('get_admin_users', {
            p_limit: 500,
            p_offset: 0
          });
        data = result.data;
        error = result.error;
      }

      if (error) {
        console.error('❌ Funciones SQL no disponibles:', error.message);
        console.log('🔄 Intentando obtener emails reales con query directo...');
        
        // Intentar obtener emails reales con un query SQL raw
        try {
          const { data: usersWithEmails, error: queryError } = await supabase
            .rpc('get_users_with_real_emails');
          
          if (!queryError && usersWithEmails) {
            console.log('✅ Emails reales obtenidos con función RPC');
            const finalUsers = usersWithEmails.map(user => ({
              ...user,
              id: user.user_id,
              status: getTrialStatus(user.trial_end_date, user.subscription_status)
            }));
            setUsers(finalUsers);
            return;
          }
        } catch (e) {
          console.log('❌ Función RPC no disponible, intentando con vista...');
        }

        // Intentar con la vista si existe
        try {
          const { data: viewData, error: viewError } = await supabase
            .from('user_profiles_with_email')
            .select('*')
            .order('created_at', { ascending: false });
          
          if (!viewError && viewData) {
            console.log('✅ Emails obtenidos desde vista user_profiles_with_email');
            const finalUsers = viewData.map(user => ({
              ...user,
              email: user.real_email || user.email,
              status: getTrialStatus(user.trial_end_date, user.subscription_status)
            }));
            setUsers(finalUsers);
            return;
          }
        } catch (e) {
          console.log('❌ Vista no disponible, usando fallback...');
        }
        
        // Fallback final: cargar sin emails reales
        const { data: user_profiles, error: user_profilesError } = await supabase
          .from('user_profiles')
          .select('*')
          .order('created_at', { ascending: false });

        console.log('📊 Respuesta de user_profiles:', { 
          data: user_profiles, 
          error: user_profilesError,
          count: user_profiles?.length 
        });

        if (user_profilesError) {
          console.error('❌ Error al cargar user_profiles:', user_profilesError);
          throw user_profilesError;
        }

        const finalUsers = (user_profiles || []).map(user => ({
          ...user,
          email: user.email || `${user.full_name?.toLowerCase().replace(' ', '.')}@mediexam.com`,
          status: getTrialStatus(user.trial_end_date, user.subscription_status)
        }));
        
        if (finalUsers.length > 0 && !finalUsers[0].email?.includes('@gmail.') && !finalUsers[0].email?.includes('@hotmail.') && !finalUsers[0].email?.includes('@outlook.')) {
          console.log('⚠️ Emails no son reales. Ejecuta add_email_column_to_profiles.sql en Supabase');
        } else {
          console.log('✅ Usando emails reales de la base de datos');
        }
        console.log('✅ Usuarios procesados:', finalUsers.length);
        console.log('📧 Emails:', finalUsers.slice(0, 3).map(u => ({ name: u.full_name, email: u.email })));
        setUsers(finalUsers);
      } else if (data) {
        // Usar datos de la función SQL que ya incluye emails reales
        const finalUsers = data.map(user => ({
          ...user,
          id: user.user_id,
          status: user.status_display || getTrialStatus(user.trial_end_date, user.subscription_status),
          trial_days_remaining: user.dias_trial_restantes,
          email: user.email // Email real desde auth.users
        }));
        
        console.log('✅ Usuarios con emails reales cargados:', finalUsers.length);
        console.log('📧 Muestra de emails:', finalUsers.slice(0, 3).map(u => ({ name: u.full_name, email: u.email })));
        setUsers(finalUsers);
      }
    } catch (error) {
      console.error('Error cargando usuarios:', error);
      setUsers([]);
    }
  };

  const loadUniversityStats = async () => {
    try {
      console.log('🎓 Cargando estadísticas de universidades...');
      
      // Intentar usar la función SQL get_university_stats
      const { data, error } = await supabase
        .rpc('get_university_stats');

      if (error) {
        console.error('❌ Función SQL no disponible, usando query directo:', error.message);
        
        // Fallback: Query directo a user_profiles
        const { data: user_profiles, error: user_profilesError } = await supabase
          .from('user_profiles')
          .select('universidad, tipo_usuario, subscription_status');

        console.log('📚 Datos de universidades obtenidos:', user_profiles?.length || 0, 'registros');

        if (!user_profilesError && user_profiles && user_profiles.length > 0) {
          // Agrupar por universidad
          const universityStats = {};
          
          user_profiles.forEach(user => {
            if (user.universidad) {
              if (!universityStats[user.universidad]) {
                universityStats[user.universidad] = {
                  name: user.universidad,
                  count: 0,
                  estudiantes: 0,
                  medicos: 0,
                  en_trial: 0,
                  pro_users: 0
                };
              }
              
              universityStats[user.universidad].count++;
              
              if (user.tipo_usuario === 'estudiante') {
                universityStats[user.universidad].estudiantes++;
              } else if (user.tipo_usuario === 'medico') {
                universityStats[user.universidad].medicos++;
              }
              
              if (user.subscription_status === 'trial') {
                universityStats[user.universidad].en_trial++;
              } else if (user.subscription_status === 'pro') {
                universityStats[user.universidad].pro_users++;
              }
            }
          });

          const sortedUniversities = Object.values(universityStats)
            .sort((a, b) => b.count - a.count);

          console.log('✅ Universidades procesadas:', sortedUniversities);
          setUniversities(sortedUniversities);
        } else {
          console.log('⚠️ No se encontraron datos de universidades');
          setUniversities([]);
        }
      } else if (data) {
        // Usar datos de la función SQL si existe
        console.log('✅ Datos de función SQL obtenidos:', data);
        const sortedUniversities = data.map(item => ({
          name: item.universidad,
          count: parseInt(item.total_usuarios) || 0,
          estudiantes: parseInt(item.estudiantes) || 0,
          medicos: parseInt(item.medicos) || 0,
          en_trial: parseInt(item.en_trial) || 0,
          pro_users: parseInt(item.pro_users) || 0
        }));
        
        setUniversities(sortedUniversities);
      }
    } catch (error) {
      console.error('❌ Error cargando universidades:', error);
      setUniversities([]);
    }
  };

  const loadCountryStats = async () => {
    try {
      console.log('🌍 Cargando estadísticas de países...');
      
      const { data, error } = await supabase
        .from('user_profiles')
        .select('pais, tipo_usuario, subscription_status');

      console.log('🗺️ Datos de países obtenidos:', data?.length || 0, 'registros');

      if (!error && data && data.length > 0) {
        // Agrupar por país con estadísticas detalladas
        const countryStats = {};
        
        data.forEach(user => {
          if (user.pais) {
            if (!countryStats[user.pais]) {
              countryStats[user.pais] = {
                name: user.pais,
                count: 0,
                estudiantes: 0,
                medicos: 0,
                en_trial: 0,
                pro_users: 0
              };
            }
            
            countryStats[user.pais].count++;
            
            if (user.tipo_usuario === 'estudiante') {
              countryStats[user.pais].estudiantes++;
            } else if (user.tipo_usuario === 'medico') {
              countryStats[user.pais].medicos++;
            }
            
            if (user.subscription_status === 'trial') {
              countryStats[user.pais].en_trial++;
            } else if (user.subscription_status === 'pro') {
              countryStats[user.pais].pro_users++;
            }
          }
        });

        const sortedCountries = Object.values(countryStats)
          .sort((a, b) => b.count - a.count);

        console.log('✅ Países procesados:', sortedCountries);
        setCountries(sortedCountries);
      } else {
        console.log('⚠️ No se encontraron datos de países');
        setCountries([]);
      }
    } catch (error) {
      console.error('❌ Error cargando países:', error);
      setCountries([]);
    }
  };

  const getTrialStatus = (trialEndDate, subscriptionStatus) => {
    if (subscriptionStatus === 'pro') return 'PRO';
    if (subscriptionStatus === 'admin') return 'ADMIN';
    
    const now = new Date();
    const endDate = new Date(trialEndDate);
    
    if (endDate > now) {
      const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
      return `Trial (${daysRemaining}d)`;
    }
    
    return 'Expirado';
  };

  const applyFilters = () => {
    let filtered = [...users];

    // Búsqueda por texto
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.universidad?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro por universidad
    if (filterUniversity !== 'all') {
      filtered = filtered.filter(user => user.universidad === filterUniversity);
    }

    // Filtro por estado
    if (filterStatus !== 'all') {
      filtered = filtered.filter(user => {
        const status = user.status || user.status_display;
        if (filterStatus === 'trial') return status?.includes('Trial') || status === 'Trial activo';
        if (filterStatus === 'expired') return status === 'Expirado' || status === 'Trial expirado';
        if (filterStatus === 'pro') return status === 'PRO';
        return true;
      });
    }

    // Filtro por tipo de usuario
    if (filterUserType !== 'all') {
      filtered = filtered.filter(user => user.tipo_usuario === filterUserType);
    }

    // Ordenamiento
    filtered.sort((a, b) => {
      if (sortBy === 'recent') return new Date(b.created_at) - new Date(a.created_at);
      if (sortBy === 'name') return a.full_name.localeCompare(b.full_name);
      if (sortBy === 'university') return a.universidad.localeCompare(b.universidad);
      if (sortBy === 'trial') return new Date(a.trial_end_date) - new Date(b.trial_end_date);
      return 0;
    });

    setFilteredUsers(filtered);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
  };

  const handleRestoreTrial = async (userId = null) => {
    const usersToRestore = userId ? [userId] : selectedUsers;
    
    if (usersToRestore.length === 0) {
      alert('Por favor selecciona al menos un usuario');
      return;
    }

    setRestoringTrial(true);
    try {
      if (usersToRestore.length === 1) {
        // Restaurar un solo usuario
        const { data, error } = await supabase
          .rpc('restore_user_trial', {
            p_user_id: usersToRestore[0],
            p_trial_days: trialDays
          });

        if (error) throw error;
        
        console.log('✅ Trial restaurado:', data);
        alert(`Trial restaurado exitosamente para ${trialDays} días`);
      } else {
        // Restaurar múltiples usuarios
        const { data, error } = await supabase
          .rpc('restore_multiple_trials', {
            p_user_ids: usersToRestore,
            p_trial_days: trialDays
          });

        if (error) throw error;
        
        console.log('✅ Trials restaurados:', data);
        alert(`${data.updated_count} trials restaurados exitosamente para ${trialDays} días`);
      }

      // Recargar datos
      await loadUsers();
      setSelectedUsers([]);
      setShowRestoreModal(false);
    } catch (error) {
      console.error('Error restaurando trial:', error);
      alert('Error al restaurar trial: ' + error.message);
    } finally {
      setRestoringTrial(false);
    }
  };

  const handleExtendTrial = async (userId) => {
    const daysToAdd = prompt('¿Cuántos días adicionales deseas agregar al trial?', '7');
    
    if (!daysToAdd || isNaN(daysToAdd)) return;

    try {
      const { data, error } = await supabase
        .rpc('extend_user_trial', {
          p_user_id: userId,
          p_additional_days: parseInt(daysToAdd)
        });

      if (error) throw error;
      
      console.log('✅ Trial extendido:', data);
      alert(`Trial extendido exitosamente con ${daysToAdd} días adicionales`);
      
      // Recargar datos
      await loadUsers();
    } catch (error) {
      console.error('Error extendiendo trial:', error);
      alert('Error al extender trial: ' + error.message);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAllUsers = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(u => u.id || u.user_id));
    }
  };

  const exportToCSV = async () => {
    try {
      // Intentar usar la función SQL export_users_csv
      const { data, error } = await supabase
        .rpc('export_users_csv');
      
      if (error) {
        console.error('Error usando función SQL, exportando manualmente:', error);
        // Fallback: exportar manualmente
        const headers = ['Nombre', 'Email', 'Universidad', 'Tipo', 'Año', 'País', 'Estado', 'Registro', 'Fin Trial'];
        const rows = filteredUsers.map(user => [
          user.full_name || '',
          user.email || '',
          user.universidad || '',
          user.tipo_usuario || '',
          user.año_estudio || 'N/A',
          user.pais || '',
          user.status || user.status_display || '',
          user.created_at ? new Date(user.created_at).toLocaleDateString() : '',
          user.trial_end_date ? new Date(user.trial_end_date).toLocaleDateString() : ''
        ]);

        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `usuarios_mediexam_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
      } else if (data) {
        // Usar datos de la función SQL
        const csvContent = data.map(row => row.csv_line).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `usuarios_mediexam_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
      }
    } catch (error) {
      console.error('Error exportando CSV:', error);
      alert('Error al exportar usuarios. Por favor intenta de nuevo.');
    }
  };

  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  if (!isOpen || !isAdmin) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 overflow-hidden">
      <div className="h-full flex flex-col bg-white dark:bg-gray-900">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 shadow-lg">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold mb-2">Panel de Administración</h1>
              <p className="text-blue-100">Sistema de gestión de usuarios - MediExamPro</p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors p-2"
            >
              <XCircle className="h-8 w-8" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="flex space-x-1 p-2">
            {['overview', 'users', 'universities', 'analytics'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${
                  activeTab === tab
                    ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-md'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-white/50 dark:hover:bg-gray-700/50'
                }`}
              >
                {tab === 'overview' && '📊 Resumen'}
                {tab === 'users' && '👥 Usuarios'}
                {tab === 'universities' && '🎓 Universidades'}
                {tab === 'analytics' && '📈 Análisis'}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <RefreshCw className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">Cargando datos...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Tab: Overview */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Stats Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <Users className="h-10 w-10 text-blue-600" />
                        <span className="text-2xl font-bold text-gray-900 dark:text-white">
                          {stats.totalUsers}
                        </span>
                      </div>
                      <h3 className="text-gray-600 dark:text-gray-400 font-medium">Total Usuarios</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                        +{stats.monthRegistrations} este mes
                      </p>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <TrendingUp className="h-10 w-10 text-green-600" />
                        <span className="text-2xl font-bold text-gray-900 dark:text-white">
                          {stats.todayRegistrations}
                        </span>
                      </div>
                      <h3 className="text-gray-600 dark:text-gray-400 font-medium">Registros Hoy</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                        +{stats.weekRegistrations} esta semana
                      </p>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <Clock className="h-10 w-10 text-orange-600" />
                        <span className="text-2xl font-bold text-gray-900 dark:text-white">
                          {stats.activeTrials}
                        </span>
                      </div>
                      <h3 className="text-gray-600 dark:text-gray-400 font-medium">Trials Activos</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                        {stats.expiredTrials} expirados
                      </p>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <Award className="h-10 w-10 text-purple-600" />
                        <span className="text-2xl font-bold text-gray-900 dark:text-white">
                          {stats.proUsers}
                        </span>
                      </div>
                      <h3 className="text-gray-600 dark:text-gray-400 font-medium">Usuarios PRO</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                        {stats.proUsers > 0 ? 
                          `${((stats.proUsers / stats.totalUsers) * 100).toFixed(1)}% conversión` : 
                          'Sin conversiones'}
                      </p>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                      Acciones Rápidas
                    </h2>
                    <div className="flex flex-wrap gap-4">
                      <button
                        onClick={handleRefresh}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                        Actualizar Datos
                      </button>
                      <button
                        onClick={exportToCSV}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <Download className="h-4 w-4" />
                        Exportar CSV
                      </button>
                      <button
                        onClick={() => setActiveTab('users')}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                      >
                        <Users className="h-4 w-4" />
                        Ver Todos los Usuarios
                      </button>
                    </div>
                  </div>

                  {/* Recent Users */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                      Últimos Registros
                    </h2>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-left border-b border-gray-200 dark:border-gray-700">
                            <th className="pb-3 text-gray-600 dark:text-gray-400">Nombre</th>
                            <th className="pb-3 text-gray-600 dark:text-gray-400">Universidad</th>
                            <th className="pb-3 text-gray-600 dark:text-gray-400">Tipo</th>
                            <th className="pb-3 text-gray-600 dark:text-gray-400">Estado</th>
                            <th className="pb-3 text-gray-600 dark:text-gray-400">Registro</th>
                          </tr>
                        </thead>
                        <tbody>
                          {users.slice(0, 5).map((user) => (
                            <tr key={user.id} className="border-b border-gray-100 dark:border-gray-700">
                              <td className="py-3 text-gray-900 dark:text-white">
                                {user.full_name}
                              </td>
                              <td className="py-3 text-gray-600 dark:text-gray-400">
                                {user.universidad}
                              </td>
                              <td className="py-3">
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  user.tipo_usuario === 'estudiante' 
                                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                                    : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                                }`}>
                                  {user.tipo_usuario}
                                </span>
                              </td>
                              <td className="py-3">
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  (user.status || user.status_display || '').includes('Trial') || (user.status || user.status_display) === 'Trial activo'
                                    ? 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300'
                                    : (user.status || user.status_display) === 'PRO'
                                    ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
                                    : 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                                }`}>
                                  {user.status || user.status_display}
                                </span>
                              </td>
                              <td className="py-3 text-gray-600 dark:text-gray-400">
                                {new Date(user.created_at).toLocaleDateString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab: Users */}
              {activeTab === 'users' && (
                <div className="space-y-6">
                  {/* Filters */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Buscar
                        </label>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Nombre o email..."
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Universidad
                        </label>
                        <select
                          value={filterUniversity}
                          onChange={(e) => setFilterUniversity(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        >
                          <option value="all">Todas</option>
                          {universities.map(uni => (
                            <option key={uni.name} value={uni.name}>
                              {uni.name} ({uni.count})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Estado
                        </label>
                        <select
                          value={filterStatus}
                          onChange={(e) => setFilterStatus(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        >
                          <option value="all">Todos</option>
                          <option value="trial">En Trial</option>
                          <option value="expired">Expirados</option>
                          <option value="pro">PRO</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Tipo
                        </label>
                        <select
                          value={filterUserType}
                          onChange={(e) => setFilterUserType(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        >
                          <option value="all">Todos</option>
                          <option value="estudiante">Estudiantes</option>
                          <option value="medico">Médicos</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Ordenar por
                        </label>
                        <select
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        >
                          <option value="recent">Más recientes</option>
                          <option value="name">Nombre</option>
                          <option value="university">Universidad</option>
                          <option value="trial">Fin de trial</option>
                        </select>
                      </div>
                    </div>

                    <div className="mt-4 flex justify-between items-center">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Mostrando {filteredUsers.length} de {users.length} usuarios
                      </p>
                      <button
                        onClick={exportToCSV}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <Download className="h-4 w-4" />
                        Exportar Filtrados
                      </button>
                    </div>
                  </div>

                  {/* Botón de acción masiva */}
                  {selectedUsers.length > 0 && (
                    <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-between">
                      <span className="text-sm text-blue-700 dark:text-blue-300">
                        {selectedUsers.length} usuario(s) seleccionado(s)
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setShowRestoreModal(true)}
                          className="px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                        >
                          Restaurar Trial
                        </button>
                        <button
                          onClick={() => setSelectedUsers([])}
                          className="px-3 py-1 bg-gray-500 text-white text-sm rounded-lg hover:bg-gray-600 transition-colors"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Users List */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-3 py-3">
                              <input
                                type="checkbox"
                                checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                                onChange={selectAllUsers}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Usuario
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Universidad
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Tipo
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Estado
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Trial
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Acciones
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {filteredUsers.map((user) => {
                            const userId = user.id || user.user_id;
                            return (
                            <React.Fragment key={userId}>
                              <tr className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                                <td className="px-3 py-4">
                                  <input
                                    type="checkbox"
                                    checked={selectedUsers.includes(userId)}
                                    onChange={() => toggleUserSelection(userId)}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div>
                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                      {user.full_name}
                                    </div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                      {user.email || 'Email no disponible'}
                                    </div>
                                    {/* Debug: Mostrar email en rojo si existe */}
                                    {user.email && (
                                      <div className="text-xs text-red-500 mt-1">
                                        📧 {user.email}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-900 dark:text-white">
                                    {user.universidad}
                                  </div>
                                  {user.año_estudio && (
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                      {user.año_estudio}° año
                                    </div>
                                  )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                    user.tipo_usuario === 'estudiante'
                                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                      : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                  }`}>
                                    {user.tipo_usuario}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                    (user.status || user.status_display || '').includes('Trial') || (user.status || user.status_display) === 'Trial activo'
                                      ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                                      : (user.status || user.status_display) === 'PRO'
                                      ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                                      : (user.status || user.status_display) === 'ADMIN'
                                      ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
                                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                  }`}>
                                    {user.status || user.status_display}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                  {new Date(user.trial_end_date).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <div className="flex items-center justify-end gap-1">
                                    <button
                                      onClick={() => handleRestoreTrial(userId)}
                                      className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
                                      title="Restaurar Trial"
                                    >
                                      Restaurar
                                    </button>
                                    <button
                                      onClick={() => handleExtendTrial(userId)}
                                      className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors"
                                      title="Extender Trial"
                                    >
                                      Extender
                                    </button>
                                    <button
                                      onClick={() => setExpandedUser(expandedUser === userId ? null : userId)}
                                      className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                                    >
                                      {expandedUser === userId ? (
                                        <ChevronUp className="h-5 w-5" />
                                      ) : (
                                        <ChevronDown className="h-5 w-5" />
                                      )}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              {expandedUser === userId && (
                                <tr>
                                  <td colSpan="7" className="px-6 py-4 bg-gray-50 dark:bg-gray-700">
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                      <div>
                                        <span className="font-medium text-gray-700 dark:text-gray-300">
                                          País:
                                        </span>
                                        <span className="ml-2 text-gray-900 dark:text-white">
                                          {user.pais}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="font-medium text-gray-700 dark:text-gray-300">
                                          Edad:
                                        </span>
                                        <span className="ml-2 text-gray-900 dark:text-white">
                                          {user.fecha_nacimiento ? `${calculateAge(user.fecha_nacimiento)} años` : 'N/A'}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="font-medium text-gray-700 dark:text-gray-300">
                                          Registro:
                                        </span>
                                        <span className="ml-2 text-gray-900 dark:text-white">
                                          {new Date(user.created_at).toLocaleString()}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="font-medium text-gray-700 dark:text-gray-300">
                                          ID:
                                        </span>
                                        <span className="ml-2 text-gray-900 dark:text-white font-mono text-xs">
                                          {(user.id || user.user_id || '').slice(0, 8)}...
                                        </span>
                                      </div>
                                    </div>
                                    {user.universidad_otra && (
                                      <div className="mt-2 text-sm">
                                        <span className="font-medium text-gray-700 dark:text-gray-300">
                                          Universidad (otra):
                                        </span>
                                        <span className="ml-2 text-gray-900 dark:text-white">
                                          {user.universidad_otra}
                                        </span>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          )})}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab: Universities */}
              {activeTab === 'universities' && (
                <div className="space-y-6">
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                      Distribución por Universidad
                    </h2>
                    {universities.length === 0 ? (
                      <div className="text-center py-8">
                        <School className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                        <p className="text-gray-500 dark:text-gray-400">
                          No hay datos de universidades disponibles
                        </p>
                        <button 
                          onClick={loadUniversityStats}
                          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Recargar Datos
                        </button>
                      </div>
                    ) : (
                    <div className="space-y-4">
                      {universities.map((uni, index) => (
                        <div key={uni.name} className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <span className="text-2xl font-bold text-gray-400 w-8">
                              {index + 1}
                            </span>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">
                                {uni.name}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {uni.count} usuarios ({((uni.count / stats.totalUsers) * 100).toFixed(1)}%)
                              </p>
                            </div>
                          </div>
                          <div className="w-64">
                            <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-4 overflow-hidden">
                              <div
                                className="bg-gradient-to-r from-blue-500 to-purple-500 h-full rounded-full transition-all duration-500"
                                style={{ width: `${(uni.count / stats.totalUsers) * 100}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    )}
                  </div>

                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
                      Distribución por País
                    </h2>
                    {countries.length === 0 ? (
                      <div className="text-center py-8">
                        <Globe className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                        <p className="text-gray-500 dark:text-gray-400">
                          No hay datos de países disponibles
                        </p>
                        <button 
                          onClick={loadCountryStats}
                          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Recargar Datos
                        </button>
                      </div>
                    ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {countries.map((country) => (
                        <div key={country.name} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <Globe className="h-5 w-5 text-gray-400" />
                            <span className="text-lg font-bold text-gray-900 dark:text-white">
                              {country.count}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {country.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {((country.count / stats.totalUsers) * 100).toFixed(1)}% del total
                          </p>
                        </div>
                      ))}
                    </div>
                    )}
                  </div>
                </div>
              )}

              {/* Tab: Analytics */}
              {activeTab === 'analytics' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Conversion Rate */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                        Tasa de Conversión
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between mb-2">
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              Trial → PRO
                            </span>
                            <span className="text-sm font-bold text-gray-900 dark:text-white">
                              {stats.totalUsers > 0 
                                ? `${((stats.proUsers / stats.totalUsers) * 100).toFixed(1)}%`
                                : '0%'}
                            </span>
                          </div>
                          <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                            <div
                              className="bg-gradient-to-r from-purple-500 to-pink-500 h-full rounded-full"
                              style={{ 
                                width: stats.totalUsers > 0 
                                  ? `${(stats.proUsers / stats.totalUsers) * 100}%`
                                  : '0%'
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* User Type Distribution */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                        Tipo de Usuarios
                      </h3>
                      <div className="space-y-4">
                        {['estudiante', 'medico'].map(tipo => {
                          const count = users.filter(u => u.tipo_usuario === tipo).length;
                          const percentage = stats.totalUsers > 0 
                            ? (count / stats.totalUsers) * 100 
                            : 0;
                          
                          return (
                            <div key={tipo}>
                              <div className="flex justify-between mb-2">
                                <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                                  {tipo}s
                                </span>
                                <span className="text-sm font-bold text-gray-900 dark:text-white">
                                  {count} ({percentage.toFixed(1)}%)
                                </span>
                              </div>
                              <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${
                                    tipo === 'estudiante'
                                      ? 'bg-gradient-to-r from-blue-500 to-cyan-500'
                                      : 'bg-gradient-to-r from-green-500 to-emerald-500'
                                  }`}
                                  style={{ width: `${percentage}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Registration Trend */}
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                      Tendencia de Registros (Últimos 7 días)
                    </h3>
                    <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                      <BarChart2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Gráfico de tendencias próximamente</p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modal de Restaurar Trial */}
      {showRestoreModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              Restaurar Trial
            </h3>
            
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Vas a restaurar el trial para {selectedUsers.length} usuario(s).
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Días de trial
              </label>
              <input
                type="number"
                value={trialDays}
                onChange={(e) => setTrialDays(parseInt(e.target.value) || 7)}
                min="1"
                max="365"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowRestoreModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                disabled={restoringTrial}
              >
                Cancelar
              </button>
              <button
                onClick={() => handleRestoreTrial()}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                disabled={restoringTrial}
              >
                {restoringTrial ? 'Restaurando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;